import { useEffect, useRef } from 'react'
import { useTheme } from '../components/providers/ThemeProvider'

/**
 * Hook to sync theme preference from server on app load.
 * This ensures the user's saved theme preference is applied when they log in.
 * 
 * IMPORTANT: If user has selected Dear Diary theme (stored in localStorage),
 * we preserve that and don't overwrite with server preference.
 */
export function useThemeSync() {
  const { setTheme, isDiary } = useTheme()
  const hasSynced = useRef(false)

  useEffect(() => {
    // Only sync once on initial mount
    if (hasSynced.current) return
    hasSynced.current = true

    // A theme choice made ON THIS DEVICE wins over the server preference.
    // The server used to clobber it on every load: the header toggle wrote
    // only localStorage, the server still said 'light', and dark mode died
    // on the next reload (owner report 2026-08-25, "make it theme ready").
    // The server preference now only seeds a device with no local choice.
    const storedTheme = localStorage.getItem('horton-theme')
    if (storedTheme) {
      console.debug('Preserving locally chosen theme:', storedTheme)
      return
    }

    const syncThemeFromServer = async () => {
      try {
        const res = await fetch('/api/profile/app-preferences', { credentials: 'include' })
        if (res.ok) {
          const data = await res.json()
          if (data.success && data.preferences?.theme_preference) {
            // Check again in case theme changed during fetch
            const currentStored = localStorage.getItem('horton-theme')
            if (currentStored === 'diary' || currentStored === 'diary-dark') {
              return // Don't overwrite diary theme
            }
            
            const themeMap: Record<string, 'light' | 'dark' | 'system'> = {
              'light': 'light',
              'dark': 'dark',
              'auto': 'system'
            }
            setTheme(themeMap[data.preferences.theme_preference] || 'system')
          }
        }
      } catch (error) {
        // Silently fail - user might not be logged in
        console.debug('Could not sync theme from server:', error)
      }
    }

    syncThemeFromServer()
  }, [setTheme, isDiary])
}

